// Where a screen lives.
//
// **There is one workspace, and it is navigated by object and task.**
// This file used to argue the opposite — two registers, `content` and
// `build`, held apart by a `build` segment and a toggle at the top of the
// sidebar — and the argument was sound about *URLs* and wrong about what
// a reader is doing. A mode is a claim that the same document is two
// documents to two people. It is not: a site owner and the agency that
// built the site both open the pages, and the difference between them is
// which controls they need in front of them, not which half of the
// address space they live in.
//
// What the mode cost, precisely:
//
//   - **Every screen had to have an opposite.** `counterpart` existed to
//     answer "the same thing, over there", and for a library, a
//     standalone component and the integrations screen the honest answer
//     was "there is nothing over there" — so the switch dropped the
//     reader at a site list. A toggle that sometimes teleports is a
//     toggle a reader stops trusting.
//   - **Two addresses for one thing.** A page had `/…/pages/:id` and
//     `/…/build/…/pages/:id`, which are the same page, and a link pasted
//     between two colleagues opened a different screen depending on which
//     of them was in which mode.
//   - **A remembered mode decided where a bare `/:tenant` landed**, which
//     is a browser-local value deciding what an address means. It was
//     scoped as tightly as it could be and it was still the one place the
//     URL was not authoritative.
//
// So: one address per thing, and the register shows in the *screen* — a
// page's blocks and a page's structure are two faces of one page, listed
// side by side, and neither is hidden behind a mode.
//
// **Site-scoped things live under the site; tenant-scoped things live
// under `design`.** That is the whole shape. A page, a record, the
// routing, the releases — all of those are facts about one site and sit
// under `/:tenant/:site`. The type library, the pattern library, the
// component library and the webhooks belong to the *tenant*: a type is
// declared once and used by every site, so filing it under a site would
// be filing it under one of the places it is used.
//
// **A mode was never a permission, and `design` is not one either.** The
// authoring surface authorises by membership; `membership.Role` has
// exactly one value, and every intent reachable from a design screen is
// one reachable from a site screen. Hiding a control is a kindness to
// the reader, not a guard on the API. What would make it a guard is a
// second role the surface itself checks, and until that exists a reader
// who types a design URL gets the design screen, correctly.

/**
 * The one reserved site id.
 *
 * A static segment outranks `/:tenant/:site`, so a site called `design`
 * would be unreachable. It replaces `build`, one for one — the reserved
 * set is still exactly one name, and the libraries and the standalone
 * component editor still sit at a depth no site route reaches.
 */
export const DESIGN = 'design'

/**
 * The query parameter the component library filters by.
 *
 * **The type is a filter, not a place.** The libraries used to be
 * fifteen nav entries and fifteen addresses — one per registered type,
 * generated from `manifest.types` — which was the data model used as an
 * information architecture: a reader looking for the photo they uploaded
 * had to know that a photo is a Media and that Media are filed beside
 * Grids and Repeating blocks. One screen, one address, and the type
 * chosen on it.
 *
 * It is a query rather than a segment because it follows the line this
 * client already draws: the path is where the reader is standing and the
 * query is what they have selected. It is still linkable — that is the
 * point of putting it in the URL at all — so every old per-type library
 * address has an exact new equivalent to redirect to, and nothing that
 * was reachable stopped being reachable.
 */
export const TYPE_FILTER = 'type'

/**
 * The signed-in reader's own account: who they are signed in as, their
 * password, their second factor, their sessions.
 *
 * **Not a tenant's**, so not under `/:tenant`. An identity holds
 * memberships in several tenants or in none, and its password is no
 * tenant's business (docs/plan/account.md §2.4). A static first segment,
 * so it outranks `/:tenant` and `account` is reserved as a tenant id, as
 * `operator` is.
 */
export const ACCOUNT = 'account'

/**
 * The guide: how to use this admin, written for the site's owner
 * (docs/plan/admin-guide.md §3.3).
 *
 * **Under the site, though nothing in it is a fact about one.** The words
 * are the same for every tenant; where they are read is not. At a first
 * segment of its own the address named no tenant and no site, so the
 * side menu lost its Site and Content groups while a customer read a
 * chapter telling them to use those groups — and a customer with one
 * site had no way back to it but the switcher. As a segment after the
 * site, the shell draws exactly as on every other site screen. A static
 * segment there collides with nothing: the other segments after a site
 * are its own screens, and a site id is never one of them.
 */
export const HELP = 'help'

const part = (value: string) => encodeURIComponent(value)

/**
 * Every address this client has.
 *
 * One object rather than two, because there is one workspace. The names
 * say the object and the task — `pageStructure`, `siteRouting` — rather
 * than the register, because the register is gone.
 *
 * **A page has four faces and they are four addresses**, each a path
 * rather than a query parameter: which face is showing is *where the
 * reader is*, and `?sel=` — a link path into the page being looked at —
 * is what they have selected. The bare page is its blocks, because that
 * is what the commoner reader came for and the shortest URL goes to
 * them. `structure` is the same page with the model showing: link ids,
 * pin state, the node panel. It is a face beside the blocks now, rather
 * than the same page in another mode, which is the whole change stated
 * in one route.
 *
 * **The bare site is the Overview**, and this is a redefinition rather
 * than an addition: `/:tenant/:site` used to be the pages. The cost is
 * real and is accepted — an owner's bookmark that meant "the pages" now
 * opens the site's state of the world, one click from the pages — and it
 * buys the thing the sidebar needs to be true: the first entry under a
 * site is where that site's address goes. `/:tenant/:site/overview`
 * redirects here, so no link that named the overview explicitly breaks.
 */
export const routes = {
  /** The reader's own account, reached from the foot of the sidebar wherever they are. */
  account: () => `/${ACCOUNT}`,

  /** The tenant's sites. A tenant holding exactly one redirects into it. */
  tenant: (tenant: string) => `/${part(tenant)}`,

  /** The site's state of the world: what is waiting to go live, and what is quietly wrong. */
  site: (tenant: string, site: string) => `/${part(tenant)}/${part(site)}`,
  /** Every page of the site, in the order they appear. */
  pages: (tenant: string, site: string) => `/${part(tenant)}/${part(site)}/pages`,
  /** One page: the blocks on it. */
  page: (tenant: string, site: string, pageId: string) =>
    `/${part(tenant)}/${part(site)}/pages/${part(pageId)}`,
  /** The same page with the model showing: the composition tree and the node panel. */
  pageStructure: (tenant: string, site: string, pageId: string) =>
    `/${part(tenant)}/${part(site)}/pages/${part(pageId)}/structure`,
  /** The page as a visitor gets it, which needs the whole screen to be truthful. */
  pagePreview: (tenant: string, site: string, pageId: string) =>
    `/${part(tenant)}/${part(site)}/pages/${part(pageId)}/preview`,
  /** The page's own facts: its name, its address, whether it is on the menu. */
  pageSettings: (tenant: string, site: string, pageId: string) =>
    `/${part(tenant)}/${part(site)}/pages/${part(pageId)}/settings`,

  /**
   * What visitors sent through the site's forms.
   *
   * **In the Site group, not in Content.** Content is what its owner puts
   * in the site; an enquiry is what a *visitor* put in it, and filing it
   * beside the record types would present the data model as an
   * information architecture again. The reader's question is "has
   * anybody contacted me", and that question has nothing to do with what
   * kind of object the answer is.
   *
   * Site-level rather than a face on the Form component, because a Form
   * is agency setup and an enquiry is the customer's own business:
   * somebody looking for a message they were sent should not have to
   * know which component collected it.
   */
  enquiries: (tenant: string, site: string) => `/${part(tenant)}/${part(site)}/enquiries`,
  /** One enquiry, whole. */
  enquiry: (tenant: string, site: string, id: string) =>
    `/${part(tenant)}/${part(site)}/enquiries/${part(id)}`,

  /** The records of one listed type, as the site's owner edits them: "the albums". */
  records: (tenant: string, site: string, type: string) =>
    `/${part(tenant)}/${part(site)}/records/${part(type)}`,
  /** One record, with what it owns. */
  record: (tenant: string, site: string, type: string, id: string) =>
    `/${part(tenant)}/${part(site)}/records/${part(type)}/${part(id)}`,
  /** The page settings of a record the site makes a page of: its address, menu name and search fields. */
  recordSettings: (tenant: string, site: string, type: string, id: string) =>
    `/${part(tenant)}/${part(site)}/records/${part(type)}/${part(id)}/settings`,
  /** The page the site makes of one record, read and framed as the page's own Preview is. */
  recordPreview: (tenant: string, site: string, type: string, id: string) =>
    `/${part(tenant)}/${part(site)}/records/${part(type)}/${part(id)}/preview`,

  /**
   * The routing tree with the model showing.
   *
   * **It keeps its own address even though the pages table is the
   * primary view of the same tree**, because it is not the same set of
   * controls: the route dialog and the generated-route dialog live here
   * and nowhere else, and so do link ids, pin state and the 404
   * placement. Collapsing it into the pages table would have been the
   * one place this reshaping lost a capability.
   */
  siteRouting: (tenant: string, site: string) => `/${part(tenant)}/${part(site)}/routing`,
  /** The site's own composition: its slot defaults and its 404. */
  siteComposition: (tenant: string, site: string) => `/${part(tenant)}/${part(site)}/composition`,
  /** The theme, the search metadata and the hostnames. */
  siteSettings: (tenant: string, site: string) => `/${part(tenant)}/${part(site)}/settings`,
  /** What is published, what is pending, and the button that publishes it. */
  siteReleases: (tenant: string, site: string) => `/${part(tenant)}/${part(site)}/releases`,
  /** The guide's contents, or one chapter of it by its slug, read beside this site's menu. */
  help: (tenant: string, site: string, chapter?: string) =>
    chapter
      ? `/${part(tenant)}/${part(site)}/${HELP}/${part(chapter)}`
      : `/${part(tenant)}/${part(site)}/${HELP}`,

  /** The component types this tenant has declared for itself. */
  types: (tenant: string) => `/${part(tenant)}/${DESIGN}/types`,
  /** What one type is — what it holds, what may go in it, what holds it. */
  type: (tenant: string, type: string) => `/${part(tenant)}/${DESIGN}/types/${part(type)}`,
  /** The patterns this tenant has declared. */
  patterns: (tenant: string) => `/${part(tenant)}/${DESIGN}/patterns`,
  /** Everything the tenant holds, of every type, or of one when a type is named. */
  components: (tenant: string, type?: string) =>
    type
      ? `/${part(tenant)}/${DESIGN}/components?${TYPE_FILTER}=${part(type)}`
      : `/${part(tenant)}/${DESIGN}/components`,
  /** One standalone component, on the face its type has. */
  component: (tenant: string, id: string) => `/${part(tenant)}/${DESIGN}/components/${part(id)}`,
  componentFields: (tenant: string, id: string) => `/${part(tenant)}/${DESIGN}/components/${part(id)}/fields`,
  componentHeldBy: (tenant: string, id: string) => `/${part(tenant)}/${DESIGN}/components/${part(id)}/held-by`,
  /** Webhooks: what this tenant has asked to be told about. */
  integrations: (tenant: string) => `/${part(tenant)}/${DESIGN}/integrations`,
}

/**
 * Which site a path is looking at, where it is looking at one.
 *
 * The navigation needs this and only this: a sidebar has to mark the
 * entry the reader is standing on, and "the current site" is a fact
 * about the URL. Reading it here rather than in the shell keeps every
 * answer about what a path *means* in one file, and keeps the shell from
 * having to know that `design` sits where a site id sits.
 */
export function siteOf(pathname: string): string | undefined {
  const site = segments(pathname)[1]
  return site === DESIGN ? undefined : site
}

/** Whether a path is one of the tenant's design screens. */
export function inDesign(pathname: string): boolean {
  return segments(pathname)[1] === DESIGN
}

function segments(pathname: string): string[] {
  return pathname.split('/').filter(Boolean).map(decodeURIComponent)
}

/**
 * The operator area's addresses.
 *
 * `operator` is a static first segment, which React Router ranks above
 * `/:tenant`, so it is reserved as a tenant id — the operator surface
 * refuses to register one by that name. The tenant under it is
 * `:tenantId`, not `:tenant`: the shell reads `:tenant` to decide which
 * tenant's manifest and sites to load, and an operator looking at a
 * customer's account is not standing inside that customer's content.
 */
export const OPERATOR = 'operator'

export const operator = {
  tenants: () => `/${OPERATOR}`,
  tenant: (tenant: string) => `/${OPERATOR}/tenants/${part(tenant)}`,
  tenantBilling: (tenant: string) => `/${OPERATOR}/tenants/${part(tenant)}/billing`,
  tenantAdministrators: (tenant: string) => `/${OPERATOR}/tenants/${part(tenant)}/administrators`,
  tenantDomains: (tenant: string) => `/${OPERATOR}/tenants/${part(tenant)}/domains`,
  /** Which of the tenant's sites have their visits collected, from where, and how that is going. */
  tenantTraffic: (tenant: string) => `/${OPERATOR}/tenants/${part(tenant)}/traffic`,
  billing: () => `/${OPERATOR}/billing`,
  plans: () => `/${OPERATOR}/plans`,
  /** The deployment's traffic connections, and the secret each one's token goes in. */
  connections: () => `/${OPERATOR}/connections`,
}

/** Whether a path is in the operator area. */
export function inOperator(pathname: string): boolean {
  return segments(pathname)[0] === OPERATOR
}
